// useEditListing.js
// Custom hook for managing EditListing state and operations

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { storage, db } from '../../services/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

const useEditListing = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newImages, setNewImages] = useState([]);
  const [previews, setPreviews] = useState([]);

  // Fetch listing data on mount
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const docRef = doc(db, "listings", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setListing(data);
          setPreviews(data.images || []);
        } else {
          alert("Listing not found. It may have been deleted.");
          navigate("/my-listings");
        }
      } catch (error) {
        alert("Unable to load listing. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id, navigate]);

  // Update listing data
  const updateListingField = (field, value) => {
    setListing(prev => ({ ...prev, [field]: value }));
  };

  // Handle image removal
  const handleRemoveImage = (url) => {
    setPreviews(prev => prev.filter(img => img !== url));
  };

  // Handle new image selection
  const handleNewImages = (files) => {
    const fileArray = Array.from(files || []);
    if (previews.length + fileArray.length > 10) {
      alert("Maximum of 10 images allowed per listing.");
      return;
    }
    setNewImages(fileArray);
    setPreviews(prev => [...prev, ...fileArray.map(f => URL.createObjectURL(f))]);
  };

  // Delete images from Firebase Storage
  const deleteStorageImages = async (imagesToDelete) => {
    for (const url of imagesToDelete) {
      try {
        const path = decodeURIComponent(url.split("/o/")[1].split("?")[0]);
        await deleteObject(ref(storage, path));
      } catch (error) {
      }
    }
  };

  // Upload new images to Firebase Storage
  const uploadNewImages = async (existingImageUrls) => {
    const uploadPromises = newImages.map(async (file) => {
      const fileRef = ref(storage, `images/listings/${id}/${Date.now()}-${file.name}`);
      await uploadBytes(fileRef, file);
      return await getDownloadURL(fileRef);
    });
    
    const newImageUrls = await Promise.all(uploadPromises);
    return [...existingImageUrls, ...newImageUrls];
  };

  // Update the listing
  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const docRef = doc(db, "listings", id);
      
      // Keep only images that are still in the preview list
      const existingImageUrls = (listing.images || []).filter(url => 
        previews.includes(url)
      );
      
      // Find images to delete
      const imagesToDelete = (listing.images || []).filter(url => 
        !previews.includes(url)
      );

      // Delete removed images from storage
      if (imagesToDelete.length > 0) {
        await deleteStorageImages(imagesToDelete);
      }

      // Upload new images and get final image URLs
      const finalImageUrls = newImages.length > 0 
        ? await uploadNewImages(existingImageUrls)
        : existingImageUrls;

      // Update the listing document
      await updateDoc(docRef, {
        title: listing.title,
        description: listing.description,
        price: parseFloat(listing.price),
        category: listing.category,
        images: finalImageUrls,
      });

      alert("Listing updated successfully.");
      navigate("/my-listings");
    } catch (error) {
      alert("Unable to update listing. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  return {
    listing,
    loading,
    updating,
    previews,
    updateListingField,
    handleRemoveImage,
    handleNewImages,
    handleUpdate
  };
};

export default useEditListing;